/* ------------------------------------------------------------------ *
 * A NAME CUT BELOW ITS FIRST TWO GRAPHEMES (card-a7 B10). A name that runs out of room is cut at its
 * END — `dir="auto"` + `truncate`, the app's standard — and it must still paint its first TWO
 * graphemes before the «…»: «C…» no longer says who (the شلم probe's `hand-end-name-room` /
 * `hud-name-room`). A box that cuts it shorter than that is what a tight form exists to widen.
 *
 * Read off the laid-out text, as the probe reads it: a Range over the first two graphemes (their
 * advance in this box's own font) plus the ellipsis, which is painted and never a node — measured in
 * the box's font on a canvas, else 0.75 em (Vazirmatn's «…»: 12.2 px of a 16.5 px line). Graphemes as
 * code points: the parse floor (Chrome 85) has no `Intl.Segmenter`, and a name's first two letters
 * are two code points in every script the table shows. Not laid out (happy-dom, a hidden page), or a
 * name not cut: 0 — nothing to widen. A tight form that frees fewer pixels than the name lacks is
 * no fix (the NARROW panels under a 150 % zoom leave the HUD's name 0 px — its tight gaps gave it 3–11
 * px, still no two letters), so each caller weighs the shortfall against what its form frees.
 * ------------------------------------------------------------------ */

let canvas: CanvasRenderingContext2D | null | undefined;

/** The «…» a `truncate` paints, in `el`'s font. */
function ellipsisWidth(el: HTMLElement): number {
  const cs = window.getComputedStyle(el);
  if (canvas === undefined) {
    try {
      canvas = document.createElement("canvas").getContext("2d");
    } catch {
      canvas = null;
    }
  }
  if (canvas !== null && cs.font !== "") {
    canvas.font = cs.font;
    const w = canvas.measureText("…").width;
    if (w > 0) return w;
  }
  return 0.75 * (parseFloat(cs.fontSize) || 0);
}

/** How many pixels `el` — a `truncate`d name — lacks for its first two graphemes and «…» (0: none). */
export function twoGraphemesShortfall(el: HTMLElement): number {
  if (el.scrollWidth <= el.clientWidth + 0.5) return 0;
  const text = el.firstChild;
  const data = text !== null && text.nodeType === Node.TEXT_NODE ? (text.textContent ?? "") : "";
  if (text === null || data === "") return 0;
  const two = Array.from(data).slice(0, 2).join("");
  const range = document.createRange();
  range.setStart(text, 0);
  range.setEnd(text, two.length);
  const w = range.getBoundingClientRect().width;
  if (!(w > 0)) return 0;
  // the box unrounded (`clientWidth` is whole pixels): the name span has no padding or border
  const short = w + ellipsisWidth(el) - el.getBoundingClientRect().width;
  return short > 0.5 ? short : 0;
}

/** Does a tight form that frees `freed` px for the name give a name cut below its first two
 *  graphemes those two? (False where the name holds them already, and where `freed` falls short.) */
export function tightGivesTwoGraphemes(el: HTMLElement, freed: number): boolean {
  const short = twoGraphemesShortfall(el);
  return short > 0 && short <= freed + 0.5;
}
